import { add, differenceInCalendarMonths, lastDayOfMonth, set } from "date-fns";
import { KeyboardEventHandler, useState } from "react";
import { today } from "../../../../utils";
export type UseKeyboardNavigationOptions = {
  monthOffset: number;
  onSelectDate?: (newDate: Date) => void;
  selectedDate?: Date;
  updateMonthOffset: (newOffset: number) => void;
};
export function useKeyboardNavigation({
  monthOffset,
  onSelectDate,
  selectedDate,
  updateMonthOffset
}: UseKeyboardNavigationOptions) {
  const [focusedDate, setFocusedDate] = useState<Date>(selectedDate ?? today);
  const currentMonth = add(today, {
    months: monthOffset
  });
  /**
   * Called when the user attempts to use keyboard navigation
   * when a DayButton is in focus
   */
  const handleKeyboardNavigation: KeyboardEventHandler = event => {
    let newFocusedDate = focusedDate;
    switch (event.key) {
      case "ArrowRight":
        newFocusedDate = add(focusedDate, {
          days: 1
        });
        break;
      case "ArrowLeft":
        newFocusedDate = add(focusedDate, {
          days: -1
        });
        break;
      case "ArrowDown":
        newFocusedDate = add(focusedDate, {
          days: 7
        });
        break;
      case "ArrowUp":
        newFocusedDate = add(focusedDate, {
          days: -7
        });
        break;
      case "PageUp":
        if (event.shiftKey) {
          newFocusedDate = add(focusedDate, {
            years: -1
          });
        } else {
          newFocusedDate = add(focusedDate, {
            months: -1
          });
        }
        break;
      case "PageDown":
        if (event.shiftKey) {
          newFocusedDate = add(focusedDate, {
            years: 1
          });
        } else {
          newFocusedDate = add(focusedDate, {
            months: 1
          });
        }
        break;
      case "Home":
        newFocusedDate = set(focusedDate, {
          date: 1
        });
        break;
      case "End":
        newFocusedDate = set(lastDayOfMonth(focusedDate), {
          hours: 0,
          minutes: 0,
          seconds: 0,
          milliseconds: 0 // eslint-disable-line sort-keys
        });
        break;
      default:
        break;
    }
    const monthDifference = differenceInCalendarMonths(set(newFocusedDate, {
      date: 1
    }), set(currentMonth, {
      date: 1
    }));
    if (monthDifference !== 0) {
      updateMonthOffset(monthOffset + monthDifference);
    }
    if (onSelectDate) {
      onSelectDate(newFocusedDate);
    }
    setFocusedDate(newFocusedDate);
  };
  return {
    focusedDate,
    handleKeyboardNavigation,
    setFocusedDate
  };
}